import { useMemo } from 'react'
import { generateMaze, solutionPath, wallsPath } from '@/lib/maze'
import './MazeArt.css'

type Props = {
  cols?: number
  rows?: number
  seed?: number
  /** draw the "breadcrumb" solution path */
  solve?: boolean
  className?: string
}

/**
 * Line-drawn maze — the brand mark as an illustration. Pure SVG, no raster,
 * inherits currentColor for walls and --accent for the trail.
 */
export default function MazeArt({ cols = 26, rows = 16, seed = 7, solve = true, className }: Props) {
  const size = 24
  const { walls, trail, w, h } = useMemo(() => {
    const m = generateMaze(cols, rows, seed)
    return {
      walls: wallsPath(m, size),
      trail: solutionPath(m, size),
      w: cols * size,
      h: rows * size,
    }
  }, [cols, rows, seed])

  return (
    <svg
      className={`maze-art ${className ?? ''}`}
      viewBox={`-1 -1 ${w + 2} ${h + 2}`}
      role="img"
      aria-label="A maze with a marked route from entrance to exit"
      shapeRendering="crispEdges"
    >
      <defs>
        <linearGradient id="maze-art-grad" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={w} y2={h}>
          <stop offset="0" className="maze-art__stop-a" />
          <stop offset="1" className="maze-art__stop-b" />
        </linearGradient>
      </defs>
      <path className="maze-art__walls" d={walls} />
      {solve && (
        <>
          <path className="maze-art__trail" d={trail} pathLength={1} />
          <circle className="maze-art__pin" cx={size / 2} cy={size / 2} r={4} />
          <circle className="maze-art__pin" cx={w - size / 2} cy={h - size / 2} r={4} />
        </>
      )}
    </svg>
  )
}
